import { api, wire, LightningElement } from "lwc";
import debounce from "lodash/debounce";
import { CategoryGroupType } from "@devforce/trailhead-client-gql";

import { EffectAdapter } from "common/effectAdapter";
import { SharedLabelsAdapter, SharedLabelsAdapterValue } from "lx/sharedLabels";
import { BooleanAttr, attrToBool } from "common/helpers";
import {
  generateFilterFormControls,
  FilterCollectionType,
} from "../util/filterHelper";

import * as locales from "./locales";

import { FormControl, FormControlOption } from "tds/formControl";

export default class extends LightningElement {
  @api queryString: string = "";
  @api resultCount: number | undefined = undefined;
  @api filterData: CategoryGroupType[] = [];
  @api activeFilters: FilterCollectionType = {};
  @api initalSortValue: string | null = null;
  @api headerText: string | null = null;
  @api description: string | null = null;
  @api minSearchChars: number | null = null;
  @api configureSortOptions: (
    options: FormControlOption[]
  ) => FormControlOption[] = (options: FormControlOption[]) => options;

  private _searchEnabled = false;
  @api
  get searchEnabled() {
    return this._searchEnabled;
  }

  set searchEnabled(newValue: BooleanAttr) {
    this._searchEnabled = attrToBool(newValue);
  }

  private searchBusy = false;
  private searchValue: string | null = null;
  private isCollapsible: boolean = false;
  private timeout: NodeJS.Timeout | null = null;
  private filters: FormControl[] = [];

  private get sortFilter(): FormControl {
    return {
      id: "sort",
      label: this.labels.sort as string,
      type: "combobox",
      value: this.initalSortValue,
      options: this.configureSortOptions([
        { label: this.labels.shared.relevance, value: "RELEVANCE" },
        { label: this.labels.shared.points, value: "MOST_POINTS" },
        { label: this.labels.shared.quickest, value: "QUICKEST" },
        { label: this.labels.shared.newest, value: "NEWEST" },
      ]),
    };
  }

  private get pageHeading() {
    if (this.headerText) {
      return this.headerText;
    } else {
      return this.searchEnabled
        ? this.labels.learn_heading
        : this.labels.search_result_heading;
    }
  }

  private get searchLabel() {
    return this.searchEnabled ? this.labels.shared.search_label : null;
  }

  private get resultCountString() {
    if (this.resultCount === undefined) return "";

    if (this.resultCount === 0) {
      return this.labels.results_string_zero;
    } else if (Math.abs(this.resultCount) === 1) {
      return this.labels.results_string_one;
    } else if (Math.abs(this.resultCount) > 1) {
      return this.labels.results_string_two_or_more;
    } else {
      return this.labels.results_string_two_or_more;
    }
  }

  @wire(SharedLabelsAdapter, { locales, options: "$labelOptions" })
  private labels: SharedLabelsAdapterValue = null!;

  private get labelOptions() {
    return {
      search_result_heading: {
        query: this.queryString,
      },
      results_string_two_or_more: {
        count: this.resultCount && this.resultCount.toLocaleString(),
      },
    };
  }

  @wire(EffectAdapter, { filterData: "$filterData" })
  private onFilterData() {
    if (this.filterData) {
      this.filters = generateFilterFormControls(
        this.filterData,
        this.activeFilters
      );
    }
  }

  connectedCallback() {
    if (this.searchEnabled) {
      this.searchValue = this.queryString;
    }
  }

  handleChange(e: CustomEvent) {
    let { id, value } = e.detail;
    if (value === "") value = null;
    this.dispatchEvent(new CustomEvent(e.type, { detail: e.detail }));
    this.filters = this.filters.map((v) => (v.id === id ? { ...v, value } : v));
  }

  handleSort(e: CustomEvent) {
    this.dispatchEvent(new CustomEvent(e.type, { detail: e.detail }));
  }

  handleClear(e: CustomEvent) {
    this.dispatchEvent(new CustomEvent(e.type, { detail: e.detail }));
    this.filters = this.filters.map((v: FormControl) => ({
      ...v,
      value: null,
    }));
  }

  handleSearch(e: CustomEvent) {
    this.searchBusy = true;
    this.searchValue = e.detail;
    this.debounceSearchEvent(e);
  }

  private debounceSearchEvent = debounce((e: CustomEvent) => {
    this.dispatchEvent(new CustomEvent(e.type, { detail: e.detail }));
    this.searchBusy = false;
  }, 1000);
}
